import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, Check, Minus } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

// ─── Module definitions ───────────────────────────────────────────────────────
// type: 'section' = standalone item, 'group' = parent with sub-items, 'subitem' = child
const MATRIX_MODULES = [
  { id: 'dashboard',         label: 'Dashboard',                    type: 'section' },
  { id: 'search-item',       label: 'Search Item',                  type: 'section' },
  { id: 'stock-availability',label: 'Stock Availability',           type: 'section' },
  { id: 'org-data',          label: 'Org Data',                     type: 'section' },
  // HR group
  { id: 'hr',                label: 'HR',                           type: 'group' },
  { id: 'attendance',        label: 'Attendance',                   type: 'subitem', parent: 'hr' },
  { id: 'leaves',            label: 'Leaves',                       type: 'subitem', parent: 'hr' },
  { id: 'master-data',       label: 'Master Data',                  type: 'section' },
  { id: 'config-settings',   label: 'Settings',                     type: 'section' },
  { id: 'inward',            label: 'Inward',                       type: 'section' },
  { id: 'inventory',         label: 'Inventory',                    type: 'section' },
  { id: 'internal-transfer', label: 'Internal Transfer',            type: 'section' },
  { id: 'part-sale-tracker', label: 'Part Sale Tracker',            type: 'section' },
  // Purchase group
  { id: 'purchase',          label: 'Purchase & Expenses',          type: 'group' },
  { id: 'purchase-bills',    label: 'Purchase Bills',               type: 'subitem', parent: 'purchase' },
  { id: 'inward-challan',    label: 'Inward Delivery Challan',      type: 'subitem', parent: 'purchase' },
  { id: 'payment-out',       label: 'Payment Out',                  type: 'subitem', parent: 'purchase' },
  { id: 'purchase-orders',   label: 'Purchase Orders',              type: 'subitem', parent: 'purchase' },
  { id: 'purchase-return',   label: 'Purchase Return / Debit Notes',type: 'subitem', parent: 'purchase' },
  // Sale group
  { id: 'sale',              label: 'Sale',                         type: 'group' },
  { id: 'sale-invoices',     label: 'Sale Invoices',                type: 'subitem', parent: 'sale' },
  { id: 'estimates',         label: 'Estimates / Quotations',       type: 'subitem', parent: 'sale' },
  { id: 'proforma-invoice',  label: 'Proforma Invoice',             type: 'subitem', parent: 'sale' },
  { id: 'payment-in',        label: 'Payment In',                   type: 'subitem', parent: 'sale' },
  { id: 'delivery-challan',  label: 'Delivery Challan',             type: 'subitem', parent: 'sale' },
  { id: 'sale-return',       label: 'Sale Return / Credit Note',    type: 'subitem', parent: 'sale' },
  // Standalone
  { id: 'transactions',      label: 'Transactions',                 type: 'section' },
  // Services group
  { id: 'services',          label: 'Services Hub',                 type: 'group' },
  { id: 'leads',             label: 'Leads',                        type: 'subitem', parent: 'services' },
  { id: 'leads-data',        label: 'Leads Data',                   type: 'subitem', parent: 'services' },
  { id: 'tickets',           label: 'Tickets',                      type: 'subitem', parent: 'services' },
  { id: 'receivables',       label: 'Receivables',                  type: 'subitem', parent: 'services' },
  { id: 'lead-sources',      label: 'Lead Source',                  type: 'subitem', parent: 'services' },
  { id: 'requirements',      label: 'Requirements',                 type: 'subitem', parent: 'services' },
  { id: 'service-steps',     label: 'Steps',                        type: 'subitem', parent: 'services' },
  { id: 'service-tasks',     label: 'Tasks',                        type: 'subitem', parent: 'services' },
];

const defaultModuleState = () => ({ view: false, add: false, edit: false, delete: false });

// Build initial all-false UI state
const buildDefaultState = () => {
  const s = {};
  MATRIX_MODULES.forEach(m => { s[m.id] = defaultModuleState(); });
  return s;
};

// Convert stored permissions_matrix → UI state
const matrixToState = (matrix) => {
  if (!matrix) return buildDefaultState();
  const s = {};
  const isAll = matrix.visibleMenuIds === '*';

  MATRIX_MODULES.forEach(mod => {
    if (mod.type === 'subitem') {
      // Sub-item visibility: parent must be visible + NOT in parent hiddenSubItems
      const parentVisible = isAll || (matrix.visibleMenuIds || []).includes(mod.parent);
      const hiddenSubs = matrix.overrides?.[mod.parent]?.hiddenSubItems || [];
      const hiddenByLegacyItemPermission = mod.id === 'lead-sources' && hiddenSubs.includes('item-list');
      const view = parentVisible && !hiddenSubs.includes(mod.id) && !hiddenByLegacyItemPermission;
      const legacyOverride = mod.id === 'lead-sources' ? matrix.overrides?.['service-items'] : null;
      const ov = matrix.overrides?.[mod.id] || legacyOverride || {};
      s[mod.id] = {
        view,
        add:    ov.canAdd    ?? matrix.canAdd    ?? false,
        edit:   ov.canEdit   ?? matrix.canEdit   ?? false,
        delete: ov.canDelete ?? matrix.canDelete ?? false,
      };
    } else {
      const view = isAll || (matrix.visibleMenuIds || []).includes(mod.id);
      const ov = matrix.overrides?.[mod.id] || {};
      s[mod.id] = {
        view,
        add:    ov.canAdd    ?? matrix.canAdd    ?? false,
        edit:   ov.canEdit   ?? matrix.canEdit   ?? false,
        delete: ov.canDelete ?? matrix.canDelete ?? false,
      };
    }
  });
  return s;
};

// Convert UI state → permissions_matrix for storage
const stateToMatrix = (moduleStates) => {
  const visibleMenuIds = [];
  const overrides = {};

  MATRIX_MODULES.forEach(mod => {
    if (mod.type === 'subitem') return; // handled via parent's hiddenSubItems
    const st = moduleStates[mod.id] || defaultModuleState();
    if (st.view) visibleMenuIds.push(mod.id);

    // Collect hidden sub-items for groups
    const hiddenSubs = [];
    if (mod.type === 'group') {
      MATRIX_MODULES.filter(m => m.parent === mod.id).forEach(child => {
        if (!moduleStates[child.id]?.view) hiddenSubs.push(child.id);
      });
    }

    const ov = { canAdd: st.add, canEdit: st.edit, canDelete: st.delete };
    if (hiddenSubs.length) ov.hiddenSubItems = hiddenSubs;
    overrides[mod.id] = ov;
  });

  // Sub-item CRUD overrides
  MATRIX_MODULES.filter(m => m.type === 'subitem').forEach(mod => {
    const st = moduleStates[mod.id] || defaultModuleState();
    overrides[mod.id] = { canAdd: st.add, canEdit: st.edit, canDelete: st.delete };
  });

  return { visibleMenuIds, canAdd: false, canEdit: false, canDelete: false, overrides };
};

// ─── Checkbox cell ────────────────────────────────────────────────────────────
const Cb = ({ checked, onChange, disabled, testId }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    data-testid={testId}
    disabled={disabled}
    className={`w-5 h-5 rounded border flex items-center justify-center transition-colors mx-auto ${
      disabled
        ? 'border-slate-200 bg-slate-50 cursor-not-allowed'
        : checked
          ? 'bg-orange-500 border-orange-500 text-white hover:bg-orange-600'
          : 'border-slate-300 bg-white hover:border-orange-400'
    }`}
  >
    {checked && !disabled && <Check className="h-3 w-3" />}
    {disabled && <Minus className="h-3 w-3 text-slate-300" />}
  </button>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export const RoleCreate = () => {
  const navigate = useNavigate();
  const { id: roleId } = useParams();
  const isEdit = Boolean(roleId);

  const [name, setName]             = useState('');
  const [description, setDesc]      = useState('');
  const [moduleStates, setMStates]  = useState(buildDefaultState);
  const [loading, setLoading]       = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState('');

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/roles`, getAuthHeaders());
        const role = res.data.find(r => r.id === roleId);
        if (!role) { setError('Role not found'); return; }
        setName(role.name);
        setDesc(role.description || '');
        setMStates(matrixToState(role.permissions_matrix));
      } catch { setError('Failed to load role'); }
      finally { setLoading(false); }
    };
    load();
  }, [isEdit, roleId]);

  // Toggle a cell; group headers cascade to all children for that field
  const toggle = (modId, field) => {
    setMStates(prev => {
      const mod = MATRIX_MODULES.find(m => m.id === modId);
      const cur = { ...prev[modId] };
      const children = mod?.type === 'group'
        ? MATRIX_MODULES.filter(m => m.parent === modId)
        : [];

      if (field === 'view') {
        if (cur.view) {
          // Turning OFF view → clear all CRUD for this module and cascade to children
          cur.view = false; cur.add = false; cur.edit = false; cur.delete = false;
          const updated = { ...prev, [modId]: cur };
          children.forEach(child => {
            updated[child.id] = { view: false, add: false, edit: false, delete: false };
          });
          return updated;
        } else {
          // Turning ON view → enable this module + cascade view ON to all children
          cur.view = true;
          const updated = { ...prev, [modId]: cur };
          children.forEach(child => {
            updated[child.id] = { ...updated[child.id], view: true };
          });
          // If subitem, also ensure parent is visible
          if (mod?.type === 'subitem') {
            updated[mod.parent] = { ...updated[mod.parent], view: true };
          }
          return updated;
        }
      } else {
        // Add / Edit / Delete field
        const newVal = !cur[field];
        cur[field] = newVal;
        if (newVal) cur.view = true; // enabling CRUD auto-enables view

        const updated = { ...prev, [modId]: cur };

        // Group header: cascade the same field to all children
        if (mod?.type === 'group') {
          children.forEach(child => {
            const childNext = { ...updated[child.id], [field]: newVal };
            if (newVal) childNext.view = true;
            updated[child.id] = childNext;
          });
        }

        // Subitem enabling CRUD → ensure parent view is on
        if (newVal && mod?.type === 'subitem') {
          updated[mod.parent] = { ...updated[mod.parent], view: true };
        }

        return updated;
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim()) { setError('Role name is required'); return; }
    const matrix = stateToMatrix(moduleStates);
    setSubmitting(true);
    try {
      if (isEdit) {
        await axios.put(`${BACKEND_URL}/api/roles/${roleId}`, { name: name.trim(), description, permissions_matrix: matrix }, getAuthHeaders());
      } else {
        await axios.post(`${BACKEND_URL}/api/roles`, { name: name.trim(), description, permissions_matrix: matrix }, getAuthHeaders());
      }
      navigate('/admin/org-data/roles');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save role');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    </div>
  );

  // Group modules for rendering
  const groups = {};
  MATRIX_MODULES.filter(m => m.type === 'group').forEach(g => {
    groups[g.id] = MATRIX_MODULES.filter(m => m.parent === g.id);
  });

  const COL_HEADER = 'text-xs font-semibold text-slate-600 uppercase tracking-wider text-center py-3 px-2';
  const ROW_BASE   = 'grid grid-cols-[1fr_80px_80px_80px_80px] items-center border-b border-slate-100';

  const renderRow = (mod, indented = false) => {
    const st = moduleStates[mod.id] || defaultModuleState();
    const viewDisabled = false;
    const crudDisabled = !st.view;
    return (
      <div key={mod.id} className={`${ROW_BASE} hover:bg-slate-50 ${indented ? 'bg-slate-50/50' : ''}`}>
        <div className={`py-3 pr-4 text-sm ${indented ? 'pl-10 text-slate-600' : 'pl-4 font-medium text-slate-800'}`}>
          {indented && <span className="text-slate-300 mr-2">└</span>}
          {mod.label}
        </div>
        <div className="text-center py-3">
          <Cb checked={st.view}   onChange={() => toggle(mod.id, 'view')}   disabled={viewDisabled}  testId={`cb-${mod.id}-view`}   />
        </div>
        <div className="text-center py-3">
          <Cb checked={st.add}    onChange={() => toggle(mod.id, 'add')}    disabled={crudDisabled}  testId={`cb-${mod.id}-add`}    />
        </div>
        <div className="text-center py-3">
          <Cb checked={st.edit}   onChange={() => toggle(mod.id, 'edit')}   disabled={crudDisabled}  testId={`cb-${mod.id}-edit`}   />
        </div>
        <div className="text-center py-3">
          <Cb checked={st.delete} onChange={() => toggle(mod.id, 'delete')} disabled={crudDisabled}  testId={`cb-${mod.id}-delete`} />
        </div>
      </div>
    );
  };

  return (
    <div data-testid="role-create-page" className="max-w-5xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/org-data/roles')}
          className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">
            {isEdit ? 'Edit Role' : 'Create Role'}
          </h1>
          <p className="text-slate-500 mt-1">Configure permissions for each module</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm">
            <AlertCircle className="h-5 w-5 flex-shrink-0" /><span>{error}</span>
          </div>
        )}

        {/* Basic info */}
        <div className="bg-white border border-slate-200 rounded-sm p-6 grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Role Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Warehouse Staff"
              data-testid="input-role-name"
              required
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDesc(e.target.value)}
              placeholder="Brief description of this role"
              data-testid="input-role-description"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
            />
          </div>
        </div>

        {/* Permissions Matrix */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
            <h2 className="text-sm font-semibold text-slate-800 uppercase tracking-wider">Permissions Matrix</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Uncheck <strong>View</strong> to hide a module entirely. CRUD is only available when View is enabled.
            </p>
          </div>

          {/* Column headers */}
          <div className={`${ROW_BASE} bg-slate-50 border-b border-slate-200`}>
            <div className="pl-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Module</div>
            <div className={COL_HEADER}>View</div>
            <div className={COL_HEADER}>Add</div>
            <div className={COL_HEADER}>Edit</div>
            <div className={COL_HEADER}>Delete</div>
          </div>

          {/* Rows */}
          {MATRIX_MODULES.map(mod => {
            if (mod.type === 'subitem') return null; // rendered under parent
            if (mod.type === 'group') {
              return (
                <div key={mod.id}>
                  {/* Group header row */}
                  <div className={`${ROW_BASE} bg-orange-50/50 border-b border-orange-100`}>
                    <div className="pl-4 py-3 text-sm font-semibold text-orange-800">{mod.label}</div>
                    <div className="text-center py-3">
                      <Cb checked={moduleStates[mod.id]?.view}   onChange={() => toggle(mod.id, 'view')}   testId={`cb-${mod.id}-view`}   />
                    </div>
                    <div className="text-center py-3">
                      <Cb checked={moduleStates[mod.id]?.add}    onChange={() => toggle(mod.id, 'add')}    disabled={!moduleStates[mod.id]?.view} testId={`cb-${mod.id}-add`}    />
                    </div>
                    <div className="text-center py-3">
                      <Cb checked={moduleStates[mod.id]?.edit}   onChange={() => toggle(mod.id, 'edit')}   disabled={!moduleStates[mod.id]?.view} testId={`cb-${mod.id}-edit`}   />
                    </div>
                    <div className="text-center py-3">
                      <Cb checked={moduleStates[mod.id]?.delete} onChange={() => toggle(mod.id, 'delete')} disabled={!moduleStates[mod.id]?.view} testId={`cb-${mod.id}-delete`} />
                    </div>
                  </div>
                  {/* Sub-items */}
                  {(groups[mod.id] || []).map(child => renderRow(child, true))}
                </div>
              );
            }
            return renderRow(mod, false);
          })}
        </div>

        {/* Submit */}
        <div className="flex gap-3 justify-end pb-8">
          <button
            type="button"
            onClick={() => navigate('/admin/org-data/roles')}
            className="px-6 py-2.5 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-role-btn"
            className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold uppercase tracking-wider px-8 py-2.5 rounded-none hover:bg-orange-600 disabled:opacity-50 transition-colors"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </form>
    </div>
  );
};
